import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { OPTIONS } from "@/lib/onboardingOptions";
import ChipGrid from "./ChipGrid";

const transition = { duration: 0.5, ease: [0.22, 1, 0.36, 1] };

export default function Step5Team({ draft, setDraft, next, prev }) {
  const set = (f) => (v) => setDraft((d) => ({ ...d, [f]: v }));
  const canContinue =
    draft.team_size && draft.founder_role && draft.hours_per_week;

  return (
    <motion.div
      data-testid="onboarding-step-5"
      key="step-5"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={transition}
      className="text-white"
    >
      <h2 className="text-[28px] font-extrabold leading-[1.1] tracking-tight md:text-[32px]">
        Your team &amp; time
      </h2>
      <p className="mt-2 text-[13px] text-white/55">
        The boardroom sizes every plan against who can actually do the work.
      </p>

      <div className="mt-6 space-y-6">
        <Group label="How many people are on the team?">
          <ChipGrid
            testIdPrefix="q-team-size"
            options={OPTIONS.team_size}
            value={draft.team_size}
            onChange={set("team_size")}
            columns={3}
          />
        </Group>

        <Group label="What best describes your role?">
          <ChipGrid
            testIdPrefix="q-founder-role"
            options={OPTIONS.founder_role}
            value={draft.founder_role}
            onChange={set("founder_role")}
            columns={2}
          />
        </Group>

        <Group label="What skills does your team currently have? (optional)">
          <ChipGrid
            testIdPrefix="q-team-skills"
            options={OPTIONS.team_skills}
            value={draft.team_skills || []}
            onChange={set("team_skills")}
            multi
            columns={3}
          />
        </Group>

        <Group label="How many hours per week can you commit?">
          <ChipGrid
            testIdPrefix="q-hours-per-week"
            options={OPTIONS.hours_per_week}
            value={draft.hours_per_week}
            onChange={set("hours_per_week")}
            columns={2}
          />
        </Group>
      </div>

      <footer className="mt-8 flex items-center justify-between">
        <button
          type="button"
          data-testid="onboarding-step-back"
          onClick={prev}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-white/60 hover:text-white"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.4} />
          Back
        </button>
        <button
          type="button"
          data-testid="onboarding-step-continue"
          disabled={!canContinue}
          onClick={next}
          className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-[13px] font-bold text-black shadow-[0_10px_30px_-10px_rgba(255,255,255,0.5)] transition-opacity disabled:opacity-40"
        >
          Continue
          <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
        </button>
      </footer>
    </motion.div>
  );
}

function Group({ label, children }) {
  return (
    <div>
      <div className="eyebrow mb-2 text-white/55">{label}</div>
      {children}
    </div>
  );
}
